// Fila local de vistorias feitas sem internet. Fica no IndexedDB do aparelho até o servidor confirmar o recebimento.
// Só o item é apagado depois da confirmação; nada é descartado em silêncio.

export type OutboxStatus = "pendente" | "enviando" | "precisa_revisao";

export interface OutboxPhoto {
  id: string;
  type: string;
  label: string;
  fileName: string;
  mimeType: string;
  originalSize: number;
  width: number;
  height: number;
  blob: Blob;
}

export interface OutboxSummary { plate: string; companyName: string; driverName: string; inspectionDate: string }

export interface OutboxInspection {
  /** UUID gerado no aparelho; é também o `clientRequestId` enviado ao servidor (idempotência). */
  id: string;
  userId: string;
  payload: Record<string, unknown>;
  summary: OutboxSummary;
  /** Fotos ainda não enviadas. Cada foto enviada é removida daqui. */
  photos: OutboxPhoto[];
  /** Preenchidos quando a vistoria já foi criada no servidor e só faltam fotos. */
  serverId?: string;
  serverCode?: string;
  status: OutboxStatus;
  attempts: number;
  nextAttemptAt: number;
  lastError?: string;
  createdAt: number;
  updatedAt: number;
}

const DB_NAME = "gestao-veicular-offline";
const DB_VERSION = 1;
const STORE = "outbox";
const CHANNEL = "gestao-veicular-outbox";

let dbPromise: Promise<IDBDatabase> | null = null;
const listeners = new Set<() => void>();
let channel: BroadcastChannel | null = null;

export const isOutboxSupported = () => typeof indexedDB !== "undefined";

function openDb(): Promise<IDBDatabase> {
  if (!isOutboxSupported()) return Promise.reject(new Error("Este navegador não permite guardar vistorias no aparelho."));
  dbPromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" });
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => { request.result.close(); dbPromise = null; };
      resolve(request.result);
    };
    request.onerror = () => { dbPromise = null; reject(request.error ?? new Error("Não foi possível abrir o armazenamento local.")); };
  });
  return dbPromise;
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
}

async function transact<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  const transaction = db.transaction(STORE, mode);
  const completed = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("Gravação local interrompida."));
    transaction.onerror = () => reject(transaction.error);
  });
  const value = await done(work(transaction.objectStore(STORE)));
  await completed;
  return value;
}

function notify() {
  listeners.forEach((listener) => listener());
  try {
    if (typeof BroadcastChannel === "undefined") return;
    if (!channel) { channel = new BroadcastChannel(CHANNEL); (channel as unknown as { unref?: () => void }).unref?.(); }
    channel.postMessage("changed");
  } catch { /* outras abas simplesmente não são avisadas */ }
}

/** Chama `listener` quando a fila muda, nesta aba ou em outra. Retorna a função de cancelamento. */
export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  let own: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== "undefined") { own = new BroadcastChannel(CHANNEL); own.onmessage = () => listener(); }
  } catch { /* sem BroadcastChannel */ }
  return () => { listeners.delete(listener); own?.close(); };
}

export async function putOutboxItem(item: OutboxInspection): Promise<void> {
  await transact("readwrite", (store) => store.put({ ...item, updatedAt: Date.now() }));
  notify();
}

export async function getOutboxItem(id: string): Promise<OutboxInspection | undefined> {
  return (await transact("readonly", (store) => store.get(id))) as OutboxInspection | undefined;
}

export async function listOutbox(userId?: string): Promise<OutboxInspection[]> {
  const all = (await transact("readonly", (store) => store.getAll())) as OutboxInspection[];
  return all.filter((item) => !userId || item.userId === userId).sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeOutboxItem(id: string): Promise<void> {
  await transact("readwrite", (store) => store.delete(id));
  notify();
}

export function newOutboxItem(input: Pick<OutboxInspection, "id" | "userId" | "payload" | "summary" | "photos"> & Partial<Pick<OutboxInspection, "serverId" | "serverCode">>): OutboxInspection {
  const now = Date.now();
  return { status: "pendente", attempts: 0, nextAttemptAt: 0, createdAt: now, updatedAt: now, ...input };
}

/** Tamanho aproximado do que será guardado (fotos), para checar o espaço antes de salvar. */
export const outboxBytes = (photos: OutboxPhoto[]) => photos.reduce((sum, photo) => sum + photo.blob.size, 0);

/** Verifica espaço e pede ao navegador que não apague os dados. Retorna mensagem de erro ou `null`. */
export async function ensureStorageFor(bytes: number): Promise<string | null> {
  try {
    if (navigator.storage?.persist) void navigator.storage.persist();
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota && estimate.usage !== undefined && estimate.usage + bytes > estimate.quota * 0.9) {
      return "O aparelho está com pouco espaço para guardar esta vistoria. Libere espaço ou conecte-se à internet para enviar as pendentes.";
    }
  } catch { /* sem API de estimativa: segue */ }
  return null;
}

export function resetOutboxForTest() {
  dbPromise = null;
  listeners.clear();
}
